import { useState, useEffect } from "react";
import { ArrowLeft, BarChart3, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface LaneInsight {
  key: string;
  originState: string;
  destinationState: string;
  equipment: string;
  loadCount: number;
  truckCount: number;
  condition: "Tight" | "Balanced" | "Soft";
  insight: string;
}

interface LaneVolumePaneProps {
  onBack: () => void;
}

export function LaneVolumePane({ onBack }: LaneVolumePaneProps) {
  const { user } = useAuth();
  const [lanes, setLanes] = useState<LaneInsight[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const fetchLaneVolume = async () => {
      try {
        const today = new Date().toISOString().split("T")[0];

        // 1. Broker's active loads
        const { data: loads } = await supabase
          .from("loads")
          .select("origin_state, destination_state, equipment_type")
          .eq("created_by", user.id)
          .eq("status", "posted")
          .gte("pickup_date", today);

        if (!loads || loads.length === 0) {
          setLanes([]);
          setLoading(false);
          return;
        }

        // 2. Group loads by lane
        const laneMap = new Map<string, { originState: string; destinationState: string; equipment: string; count: number }>();
        for (const l of loads) {
          const key = `${l.origin_state}|${l.destination_state}|${l.equipment_type}`;
          const existing = laneMap.get(key);
          if (existing) {
            existing.count++;
          } else {
            laneMap.set(key, {
              originState: l.origin_state,
              destinationState: l.destination_state,
              equipment: l.equipment_type,
              count: 1,
            });
          }
        }

        // 3. Fetch active truck postings
        const { data: trucks } = await supabase
          .from("truck_postings")
          .select("equipment_type, origin_location, destination_location")
          .in("status", ["active", "available"])
          .gte("available_to", today);

        // 4. Group trucks by extracted states + equipment
        const truckMap = new Map<string, number>();
        for (const t of (trucks || [])) {
          const oState = extractState(t.origin_location);
          const dState = extractState(t.destination_location);
          if (!oState) continue; // skip if we can't determine origin state
          const eq = t.equipment_type || "";
          // If we have destination state, use full key; otherwise origin-only key
          if (dState) {
            const fullKey = `${oState}|${dState}|${eq}`;
            truckMap.set(fullKey, (truckMap.get(fullKey) || 0) + 1);
          }
          // Also always count under origin-only key for fallback
          const originKey = `${oState}|*|${eq}`;
          truckMap.set(originKey, (truckMap.get(originKey) || 0) + 1);
        }

        // 5. Build insights
        const results: LaneInsight[] = [];
        for (const [key, lane] of laneMap) {
          const fullKey = `${lane.originState}|${lane.destinationState}|${lane.equipment}`;
          // Try exact match first, fallback to origin-only
          let truckCount = truckMap.get(fullKey) ?? truckMap.get(`${lane.originState}|*|${lane.equipment}`) ?? 0;

          let condition: LaneInsight["condition"];
          let insight: string;

          if (lane.count > truckCount * 1.5) {
            condition = "Tight";
            insight = "Capacity shortage";
          } else if (truckCount > lane.count * 1.5) {
            condition = "Soft";
            insight = "Truck heavy lane";
          } else {
            condition = "Balanced";
            insight = "Market stable";
          }

          results.push({
            key,
            originState: lane.originState,
            destinationState: lane.destinationState,
            equipment: formatEquipment(lane.equipment),
            loadCount: lane.count,
            truckCount,
            condition,
            insight,
          });
        }

        // Sort: Tight first, then Balanced, then Soft
        const order = { Tight: 0, Balanced: 1, Soft: 2 };
        results.sort((a, b) => order[a.condition] - order[b.condition] || b.loadCount - a.loadCount);

        setLanes(results.slice(0, 21));
      } catch (err) {
        console.error("LaneVolumePane error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLaneVolume();
  }, [user]);

  const conditionColor: Record<LaneInsight["condition"], string> = {
    Tight: "text-destructive",
    Balanced: "text-success",
    Soft: "text-yellow-500",
  };

  return (
    <div className="flex flex-col gap-0 h-full rounded-lg border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-card)" }}>
      {/* Header */}
      <div className="flex items-center gap-2 border-b px-3 py-2.5 bg-muted/20">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to My Loads
        </button>
        <span className="text-[10px] text-muted-foreground">|</span>
        <div className="flex items-center gap-1.5">
          <BarChart3 className="h-3.5 w-3.5 text-accent" />
          <h4 className="text-caption font-bold text-foreground">Lane Volume Insights</h4>
        </div>
        <span className="ml-auto text-[10px] text-muted-foreground">{lanes.length} lane{lanes.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-[12px]">Analyzing lane volume…</span>
          </div>
        ) : lanes.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <BarChart3 className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-[13px] font-semibold text-muted-foreground">No active lanes to analyze</p>
            <p className="text-[11px] text-muted-foreground">Post loads to see lane volume insights across your active routes.</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider">Lane</TableHead>
                  <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider">Equipment</TableHead>
                  <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider text-center">Active Loads</TableHead>
                  <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider text-center">Active Trucks</TableHead>
                  <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider">Condition</TableHead>
                  <TableHead className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider">Insight</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lanes.map((lane) => (
                  <TableRow key={lane.key} className="group">
                    <TableCell className="px-3 py-2 text-[11px] font-medium text-foreground whitespace-nowrap">
                      {lane.originState} → {lane.destinationState}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-[11px] text-muted-foreground">{lane.equipment}</TableCell>
                    <TableCell className="px-3 py-2 text-[11px] font-semibold text-foreground tabular-nums text-center">{lane.loadCount}</TableCell>
                    <TableCell className="px-3 py-2 text-[11px] font-semibold text-foreground tabular-nums text-center">{lane.truckCount}</TableCell>
                    <TableCell className="px-3 py-2">
                      <span className={`text-[11px] font-bold ${conditionColor[lane.condition]}`}>
                        {lane.condition}
                      </span>
                    </TableCell>
                    <TableCell className="px-3 py-2 text-[11px] text-muted-foreground">{lane.insight}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="px-3 py-2 text-[9px] text-muted-foreground border-t">
              v1 matching: origin state + destination state (best-effort) + equipment type. Results may be origin-biased when truck destination data is unavailable.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// --- Helpers ---

function extractState(location: string): string | null {
  if (!location) return null;
  const match = location.match(/,\s*([A-Z]{2})\b/);
  if (match) return match[1];
  const stateMatch = location.match(/\b([A-Z]{2})\b/);
  return stateMatch ? stateMatch[1] : null;
}

function formatEquipment(eq: string): string {
  const map: Record<string, string> = {
    van: "Van", reefer: "Reefer", flatbed: "Flatbed",
    step_deck: "Step Deck", power_only: "Power Only",
    box_truck: "Box Truck", hotshot: "Hotshot",
    conestoga: "Conestoga", rgn: "RGN", tanker: "Tanker",
    intermodal: "Intermodal", car_hauler: "Car Hauler",
    dump_trailer: "Dump", lowboy: "Lowboy", other: "Other",
  };
  return map[eq] || eq;
}
